/* Media blocks: vertical rotating heading.
   Reuses the existing "Show scrolling heading" toggle as the activation switch.
   Separate alternatives with ||. When the delimiter is inside a formatted
   selection, only that selected fragment rotates; otherwise the full heading does. */
(() => {
  const SELECTOR = '.shopify-section--media-blocks h2 > marquee-text.scrolling-content';
  const DELIMITER = '||';
  const CHANGE_INTERVAL = 2800;
  const TRANSITION_DURATION = 420;

  const splitPhrases = (html) =>
    html
      .split(DELIMITER)
      .map((phrase) => phrase.trim())
      .filter(Boolean);

  const textFromHtml = (html) => {
    const template = document.createElement('template');
    template.innerHTML = html;
    return template.content.textContent.replace(/\s+/g, ' ').trim();
  };

  const createRotator = (phrases, contentFactory, mode) => {
    const rotator = document.createElement('span');
    rotator.className = `distopion-rotating-heading distopion-rotating-heading--${mode}`;
    rotator.setAttribute('role', 'text');
    rotator.setAttribute(
      'aria-label',
      phrases.map(textFromHtml).filter(Boolean).join(' / ')
    );

    phrases.forEach((phrase, index) => {
      const item = document.createElement('span');
      item.className = `distopion-rotating-heading__item${index === 0 ? ' is-active' : ''}`;
      item.setAttribute('aria-hidden', 'true');
      item.append(contentFactory(phrase));
      rotator.append(item);
    });

    return rotator;
  };

  const startRotator = (rotator) => {
    const items = Array.from(rotator.children);
    if (items.length < 2) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let currentIndex = items.findIndex((item) => item.classList.contains('is-active'));
    if (currentIndex < 0) currentIndex = 0;

    let timer = 0;
    let cleanupTimer = 0;
    let frameOne = 0;
    let frameTwo = 0;
    let isIntersecting = !('IntersectionObserver' in window);
    let observer = null;
    let destroyed = false;

    const clearScheduledWork = () => {
      window.clearTimeout(timer);
      window.clearTimeout(cleanupTimer);
      window.cancelAnimationFrame(frameOne);
      window.cancelAnimationFrame(frameTwo);
      timer = 0;
      cleanupTimer = 0;
      frameOne = 0;
      frameTwo = 0;
    };

    const normalize = () => {
      items.forEach((item, index) => {
        item.classList.toggle('is-active', index === currentIndex);
        item.classList.remove('is-exiting');
      });
    };

    const pauseAndNormalize = () => {
      clearScheduledWork();
      normalize();
    };

    const canRun = () =>
      !destroyed &&
      rotator.isConnected &&
      !document.hidden &&
      isIntersecting &&
      !reducedMotion.matches;

    const schedule = () => {
      window.clearTimeout(timer);
      timer = 0;

      if (!canRun()) return;
      timer = window.setTimeout(advance, CHANGE_INTERVAL);
    };

    const advance = () => {
      timer = 0;

      if (!canRun()) {
        pauseAndNormalize();
        return;
      }

      const current = items[currentIndex];
      const nextIndex = (currentIndex + 1) % items.length;
      const next = items[nextIndex];

      /* A browser can throttle timers/rAF while a tab is hidden or while this
         section is offscreen. Always clear stale transition state first so a
         resumed animation can never leave two or more phrases visible. */
      items.forEach((item, index) => {
        if (index !== currentIndex) item.classList.remove('is-active');
        item.classList.remove('is-exiting');
      });

      current.classList.add('is-exiting');
      current.classList.remove('is-active');
      next.classList.remove('is-exiting');

      frameOne = window.requestAnimationFrame(() => {
        frameOne = 0;
        frameTwo = window.requestAnimationFrame(() => {
          frameTwo = 0;

          if (!canRun()) {
            pauseAndNormalize();
            return;
          }

          next.classList.add('is-active');
          currentIndex = nextIndex;

          cleanupTimer = window.setTimeout(() => {
            cleanupTimer = 0;
            normalize();
            schedule();
          }, TRANSITION_DURATION + 40);
        });
      });
    };

    const resume = () => {
      pauseAndNormalize();
      schedule();
    };

    const onVisibilityChange = () => {
      if (document.hidden) {
        pauseAndNormalize();
      } else if (isIntersecting) {
        resume();
      }
    };

    const onPageShow = () => {
      if (isIntersecting && !document.hidden) {
        resume();
      } else {
        pauseAndNormalize();
      }
    };

    const onPageHide = () => pauseAndNormalize();

    const onReducedMotionChange = () => {
      if (reducedMotion.matches) {
        pauseAndNormalize();
      } else if (isIntersecting && !document.hidden) {
        resume();
      }
    };

    const destroy = () => {
      if (destroyed) return;
      destroyed = true;
      clearScheduledWork();
      if (observer) observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('pagehide', onPageHide);
      if (reducedMotion.removeEventListener) {
        reducedMotion.removeEventListener('change', onReducedMotionChange);
      } else if (reducedMotion.removeListener) {
        reducedMotion.removeListener(onReducedMotionChange);
      }
    };

    normalize();
    rotator._distopionRotatorCleanup = destroy;

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('pagehide', onPageHide);

    if (reducedMotion.addEventListener) {
      reducedMotion.addEventListener('change', onReducedMotionChange);
    } else if (reducedMotion.addListener) {
      reducedMotion.addListener(onReducedMotionChange);
    }

    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        ([entry]) => {
          isIntersecting = Boolean(entry && entry.isIntersecting);

          if (isIntersecting && !document.hidden) {
            resume();
          } else {
            pauseAndNormalize();
          }
        },
        { threshold: 0.01 }
      );
      observer.observe(rotator);
    } else {
      schedule();
    }
  };

  const setupRotator = (marquee) => {
    const heading = marquee.closest('h2');
    if (!heading || heading.dataset.distopionRotatingHeading === 'true') return;

    const sourceWrapper = marquee.querySelector('.scrolling-content__text');
    if (!sourceWrapper) return;

    /* Use the deepest formatted element containing the delimiters. This avoids
       splitting outer HTML such as <em><strong>one || two</strong></em>, which
       could otherwise make formatting inconsistent between phrases. */
    const formattedSources = Array.from(
      sourceWrapper.querySelectorAll('[data-distopion-rotate-fragment], em, strong')
    );
    const matchingSources = formattedSources.filter((element) =>
      element.innerHTML.includes(DELIMITER)
    );
    const selectedSource = matchingSources[matchingSources.length - 1];

    if (selectedSource) {
      const phrases = splitPhrases(selectedSource.innerHTML);
      const renderedHeading = sourceWrapper.firstElementChild;

      if (phrases.length > 1 && renderedHeading) {
        const renderedClone = renderedHeading.cloneNode(true);
        renderedClone.classList.add('distopion-rotating-heading__line');
        renderedClone.classList.remove('text-balance');

        const selectedIndex = formattedSources.indexOf(selectedSource);
        const cloneFormattedSources = Array.from(
          renderedClone.querySelectorAll('[data-distopion-rotate-fragment], em, strong')
        );
        const selectedClone = cloneFormattedSources[selectedIndex];

        if (selectedClone) {
          const rotator = createRotator(
            phrases,
            (phrase) => {
              const formatClone = selectedSource.cloneNode(false);
              formatClone.removeAttribute && formatClone.removeAttribute('id');
              formatClone.innerHTML = phrase;
              return formatClone;
            },
            'inline'
          );

          selectedClone.replaceWith(rotator);
          heading.replaceChildren(renderedClone);
          heading.dataset.distopionRotatingHeading = 'true';
          startRotator(rotator);
          return;
        }
      }
    }

    const sourceTemplate =
      sourceWrapper.querySelector('highlighted-heading') ||
      sourceWrapper.querySelector(':scope > span') ||
      sourceWrapper;
    const sourceHtml = sourceTemplate.innerHTML || '';

    if (!sourceHtml.includes(DELIMITER)) return;

    const phrases = splitPhrases(sourceHtml);
    if (phrases.length < 2) return;

    const rotator = createRotator(
      phrases,
      (phrase) => {
        if (sourceTemplate === sourceWrapper) {
          const span = document.createElement('span');
          span.innerHTML = phrase;
          return span;
        }

        const clone = sourceTemplate.cloneNode(false);
        clone.removeAttribute && clone.removeAttribute('id');
        clone.innerHTML = phrase;
        return clone;
      },
      'block'
    );

    heading.replaceChildren(rotator);
    heading.dataset.distopionRotatingHeading = 'true';
    startRotator(rotator);
  };

  const init = (root = document) => {
    if (root instanceof Element && root.matches(SELECTOR)) setupRotator(root);
    root.querySelectorAll && root.querySelectorAll(SELECTOR).forEach(setupRotator);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:reorder', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll &&
      event.target.querySelectorAll('.distopion-rotating-heading').forEach((rotator) => {
        if (typeof rotator._distopionRotatorCleanup === 'function') {
          rotator._distopionRotatorCleanup();
        }
      });
  });
})();